import { ICurrentClient } from '@/shared/interfaces/current-client';

const DEFAULT_TIMEZONE = 'Canada/Eastern';

// Maps Canadian province and US state codes to the matching entries in `timezoneOptions`
// (static-select-options.ts). States/provinces spanning multiple zones use their majority zone.
const PROVINCE_STATE_TIMEZONE_MAP: Record<string, string> = {
  AB: 'Canada/Mountain',
  BC: 'Canada/Pacific',
  MB: 'Canada/Central',
  NB: 'Canada/Atlantic',
  NL: 'Canada/Newfoundland',
  NS: 'Canada/Atlantic',
  NT: 'Canada/Mountain',
  NU: 'Canada/Eastern',
  ON: 'Canada/Eastern',
  PE: 'Canada/Atlantic',
  QC: 'Canada/Eastern',
  SK: 'Canada/Saskatchewan',
  YT: 'Canada/Yukon',
  AL: 'US/Central',
  AK: 'US/Alaska',
  AZ: 'US/Arizona',
  AR: 'US/Central',
  CA: 'US/Pacific',
  CO: 'US/Mountain',
  CT: 'US/Eastern',
  DC: 'US/Eastern',
  DE: 'US/Eastern',
  FL: 'US/Eastern',
  GA: 'US/Eastern',
  HI: 'US/Hawaii',
  IA: 'US/Central',
  ID: 'US/Mountain',
  IL: 'US/Central',
  IN: 'US/Eastern',
  KS: 'US/Central',
  KY: 'US/Eastern',
  LA: 'US/Central',
  MA: 'US/Eastern',
  MD: 'US/Eastern',
  ME: 'US/Eastern',
  MI: 'US/Michigan',
  MN: 'US/Central',
  MO: 'US/Central',
  MS: 'US/Central',
  MT: 'US/Mountain',
  NC: 'US/Eastern',
  ND: 'US/Central',
  NE: 'US/Central',
  NH: 'US/Eastern',
  NJ: 'US/Eastern',
  NM: 'US/Mountain',
  NV: 'US/Pacific',
  NY: 'US/Eastern',
  OH: 'US/Eastern',
  OK: 'US/Central',
  OR: 'US/Pacific',
  PA: 'US/Eastern',
  RI: 'US/Eastern',
  SC: 'US/Eastern',
  SD: 'US/Central',
  TN: 'US/Central',
  TX: 'US/Central',
  UT: 'US/Mountain',
  VA: 'US/Eastern',
  VT: 'US/Eastern',
  WA: 'US/Pacific',
  WI: 'US/Central',
  WV: 'US/Eastern',
  WY: 'US/Mountain',
};

/**
 * Resolves a client's timezone, preferring the client's own `timeZone` setting and
 * falling back to inferring it from the client's address (city's province/state code)
 * when that setting is empty. Falls back to `Canada/Eastern` when neither is available.
 */
export const inferTimezoneFromAddress = (client: Pick<ICurrentClient, 'timeZone' | 'city'>): string => {
  if (client.timeZone) return client.timeZone;

  const provinceCode = client.city?.province?.provinceCode?.toUpperCase();

  return (provinceCode && PROVINCE_STATE_TIMEZONE_MAP[provinceCode]) || DEFAULT_TIMEZONE;
};
